import { type FC, type ReactNode, useCallback, useEffect } from 'react';
import { useLocation } from 'wouter';

import { GameTile as Tile } from '@atlas/components';

import { AuthStatus, useClientProfile, useLanguage, usePopups } from 'store';
import { getMediaUrl } from 'utils/assets';
import { getGameUrl } from 'utils/casino';
import pushToDataLayer, { btag } from 'utils/dataLayer';
import useLayout, { LayoutType } from 'utils/layout';
import { useTranslation } from 'utils/translations';

export type GameTileProps = {
  className?: string;
  id: number;
  name: string;
  iconUrl: string;
  isDemoAvailable: boolean;
  favoriteIcon?: ReactNode;
  providerName: string;
  favoritesCount: number;
};

const GameTile: FC<GameTileProps> = ({
  id,
  isDemoAvailable,
  name,
  iconUrl,
  favoriteIcon,
  className,
  providerName,
  favoritesCount,
}) => {
  const [, navigate] = useLocation();
  const [{ status, data }] = useClientProfile();
  const playButtonLabel = useTranslation('play');
  const demoButtonLabel = useTranslation('demo');
  const layout = useLayout();
  const { addPopup } = usePopups();
  const [
    {
      current: { code: lang },
    },
  ] = useLanguage();

  const playGame = useCallback(
    (demo?: boolean) => {
      const gameUrl = getGameUrl(id, { demo, providerName });
      navigate(`~${gameUrl}`);
    },
    [navigate, id, providerName],
  );

  const playButtonHandler = useCallback(() => {
    if (status === AuthStatus.signedIn && data) {
      playGame();

      pushToDataLayer(
        'open_casino_game',
        {
          providerName,
          name,
          favoritesCount,
          demoAvailable: isDemoAvailable,
          gameId: id,
          iconUrl: iconUrl ? getMediaUrl(iconUrl, 'game') : null,
          backgroundUrl: !iconUrl ? getMediaUrl('yellow/game-tile.webp') : null,
          language: lang,
          client: {
            id: data.client.id,
          },
        },
        btag,
      );
    } else {
      addPopup({
        type: 'default',
        contentType: 'auth',
        immediate: true,
      });
    }
  }, [
    status,
    playGame,
    id,
    name,
    data,
    isDemoAvailable,
    favoritesCount,
    iconUrl,
    lang,
    providerName,
    addPopup,
  ]);

  const demoButtonHandler = useCallback(() => {
    playGame(true);

    pushToDataLayer(
      'open_demo_game',
      {
        providerName,
        name,
        favoritesCount,
        demoAvailable: isDemoAvailable,
        gameId: id,
        iconUrl: iconUrl ? getMediaUrl(iconUrl, 'game') : null,
        backgroundUrl: !iconUrl ? getMediaUrl('yellow/game-tile.webp') : null,
        language: lang,
      },
      btag,
    );
  }, [
    playGame,
    name,
    id,
    isDemoAvailable,
    favoritesCount,
    iconUrl,
    lang,
    providerName,
  ]);

  const clickGameTileHandler = useCallback(() => {
    if (layout !== LayoutType.mobile || isDemoAvailable) return;
    playButtonHandler();
  }, [layout, isDemoAvailable, playButtonHandler]);

  useEffect(() => {
    const { gameId, demo, providerName } = history.state || {};

    if (gameId) {
      const gameUrl = getGameUrl(gameId, { demo, providerName });
      navigate(`~${gameUrl}`, { replace: true });
    }
  }, [navigate]);

  const imageUrl = iconUrl
    ? getMediaUrl(iconUrl, 'game')
    : getMediaUrl('yellow/game-tile.webp');

  return (
    <Tile
      className={className}
      title={name}
      image={imageUrl}
      playButtonLabel={playButtonLabel}
      playButtonHandler={playButtonHandler}
      demoButtonLabel={isDemoAvailable ? demoButtonLabel : undefined}
      demoButtonHandler={demoButtonHandler}
      onClick={clickGameTileHandler}
      favoriteIcon={favoriteIcon}
    />
  );
};

export default GameTile;
